import { useEffect, useState } from "react";
import { Download, ArrowRight } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    isIOS,
    isStandalone,
    InstallLead,
    InstallActions,
} from "@/components/pwa/installShared";

// Permanent iOS-only card shown when the site is opened in the browser
// (Safari / Chrome iOS / Edge iOS / iPad). Hidden as soon as the site is
// launched from the Home-screen icon (standalone). Fully independent from
// the popup-one-shot / banner / A2-A3-A4 pipeline: it never reads or writes
// cn_install_completed, cn_install_popup_shown, dismissal keys, or the
// BroadcastChannel. It just reuses the existing iOS instructions modal.
export default function InstallHomeCardIOS() {
    // Re-evaluate visibility on mount + when the app becomes standalone at
    // runtime (rare on iOS but safe).
    const [visible, setVisible] = useState(() => isIOS() && !isStandalone());
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const recompute = () => setVisible(isIOS() && !isStandalone());
        try {
            const mql = window.matchMedia("(display-mode: standalone)");
            mql.addEventListener?.("change", recompute);
            return () => mql.removeEventListener?.("change", recompute);
        } catch { /* noop */ }
    }, []);

    if (!visible) return null;

    return (
        <section className="bg-white" data-testid="home-install-card-ios-section">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    data-testid="home-install-card-ios"
                    className="group relative w-full text-left overflow-hidden rounded-3xl bg-gradient-to-r from-primary via-primary to-accent px-5 py-6 sm:px-8 sm:py-8 lg:px-10 lg:py-10 shadow-[0_10px_30px_-12px_rgba(48,131,201,0.55)] hover:shadow-[0_20px_45px_-15px_rgba(245,149,71,0.55)] hover:-translate-y-0.5 transition-all duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-accent/40"
                >
                    <div className="pointer-events-none absolute -top-24 -right-16 size-72 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
                    <div className="pointer-events-none absolute -bottom-20 -left-10 size-56 rounded-full bg-accent/30 blur-3xl" aria-hidden="true" />

                    <div className="relative flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-6">
                        <img
                            src="/apple-touch-icon.png"
                            alt="CondominioNow"
                            width={72}
                            height={72}
                            className="size-14 sm:size-16 rounded-2xl shrink-0 ring-2 ring-white/40 shadow-[0_8px_24px_-6px_rgba(0,0,0,0.35)]"
                        />

                        <div className="min-w-0 flex-1">
                            <div className="text-[11px] uppercase tracking-[0.18em] text-white/80 font-semibold">
                                Installa l'app
                            </div>
                            <div className="mt-1 font-serif text-2xl sm:text-3xl font-semibold text-white leading-tight">
                                <span aria-hidden="true">📱</span> Porta CondominioNow sempre con te
                            </div>
                            <p className="mt-2 text-sm sm:text-base text-white/90 leading-relaxed max-w-2xl">
                                Le risposte ai tuoi dubbi condominiali sempre a portata di mano.
                            </p>
                        </div>

                        <span className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-primary font-semibold px-5 py-3 sm:px-6 sm:py-3.5 shadow-[0_8px_20px_-6px_rgba(0,0,0,0.35)] group-hover:gap-3 group-hover:bg-accent group-hover:text-white transition-all duration-300 shrink-0 self-stretch sm:self-auto text-center">
                            <Download className="size-4" strokeWidth={2.5} />
                            <span>Scopri come installare</span>
                            <ArrowRight className="size-4" strokeWidth={2.5} />
                        </span>
                    </div>
                </button>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    className="sm:max-w-md"
                    data-testid="home-install-card-ios-modal"
                    aria-describedby="install-card-ios-lead"
                >
                    <DialogHeader className="text-left">
                        <div className="flex items-start gap-3">
                            <img
                                src="/apple-touch-icon.png"
                                alt="CondominioNow"
                                width={48}
                                height={48}
                                className="size-12 rounded-xl shrink-0 border border-slate-200"
                            />
                            <div className="min-w-0">
                                <div className="text-xs uppercase tracking-widest text-primary font-semibold">
                                    Installa l'app
                                </div>
                                <DialogTitle className="mt-0.5 font-serif text-lg font-semibold text-slate-900 leading-tight">
                                    CondominioNow sulla tua Home
                                </DialogTitle>
                            </div>
                        </div>
                        <DialogDescription asChild>
                            <span id="install-card-ios-lead" className="block">
                                <InstallLead mode="ios" />
                            </span>
                        </DialogDescription>
                    </DialogHeader>

                    <InstallActions
                        mode="ios"
                        deferredPrompt={null}
                        installing={false}
                        showSamsungFallback={false}
                        setShowSamsungFallback={() => {}}
                        copied={false}
                        onNativeInstall={() => {}}
                        onOpenChrome={() => {}}
                        onCopyLink={() => {}}
                        onDismiss={null}
                        testIdPrefix="home-install-card-ios-modal"
                    />
                </DialogContent>
            </Dialog>
        </section>
    );
}
